import React, { useEffect, useState } from "react";
import { goToCheckout } from "@/lib/checkout";
import { toast } from "sonner";
import { api, API, getToken } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { formatMoney } from "@/lib/money";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileText, Download, Receipt, CreditCard, BellRing, CalendarClock, Building2, ChevronDown, FileSpreadsheet, Eye } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

function fmtPeriod(period, locale) {
  if (!period) return "";
  const [y, m] = period.split("-");
  try { return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(locale || "en", { month: "long", year: "numeric" }); }
  catch { return `${m}/${y}`; }
}

function InvoicePreview() {
  const { t, lang } = useLang();
  const [p, setP] = useState(undefined);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    (async () => { try { setP(await api("/auth/me/monthly-invoice-preview")); } catch { setP(null); } })();
  }, []);
  if (!p || !p.billable || !(p.total_ttc > 0)) return null;
  return (
    <div className="mb-8" data-testid="invoice-preview-section">
      <div className="flex items-center gap-2 mb-3">
        <Eye size={18} className="text-findo-indigo" />
        <h2 className="font-display font-bold text-slate-900">{t("inv2.previewTitle")} {fmtPeriod(p.period, lang)}</h2>
      </div>
      <div className="bg-white rounded-2xl border border-findo-indigo/20 card-shadow overflow-hidden" data-testid="invoice-preview-card">
        <div className="flex items-center justify-between gap-3 p-4">
          <div>
            <p className="text-xs text-slate-400">{t("inv2.estimate").replace("{n}", p.establishment_count)}</p>
            <p className="text-2xl font-bold text-findo-navy mt-0.5" data-testid="invoice-preview-total">{formatMoney(p.total_ttc, lang, p.currency || "EUR")} <span className="text-xs font-normal text-slate-400">{t("hc.inclVat")}</span></p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} className="rounded-full h-8 text-xs font-semibold" data-testid="invoice-preview-toggle">
            <ChevronDown size={14} className={`mr-1 transition-transform ${open ? "rotate-180" : ""}`} /> {t("inv2.detailByEst")}
          </Button>
        </div>
        {open && (
          <div className="bg-slate-50 px-4 py-3 space-y-1.5 border-t border-border" data-testid="invoice-preview-lines">
            {p.lines.map((l, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs">
                {l.kind === "establishment" ? <Building2 size={12} className="text-slate-400 shrink-0" /> : <Receipt size={12} className="text-findo-indigo shrink-0" />}
                <span className="flex-1 text-slate-600 truncate">{l.description}</span>
                <span className={l.included ? "text-slate-400" : "font-semibold text-slate-700"}>
                  {l.included ? t("inv2.included") : `${formatMoney(l.unit_ht, lang)} HT`}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between text-xs pt-1.5 border-t border-border mt-1">
              <span className="text-slate-500">{t("inv2.totalHtTva")}</span>
              <span className="text-slate-600">{formatMoney(p.total_ht, lang)} · {formatMoney(p.total_tva, lang)}</span>
            </div>
            <div className="flex items-center justify-between text-sm font-bold text-findo-navy">
              <span>{t("inv2.totalTtc")}</span><span>{formatMoney(p.total_ttc, lang, p.currency || "EUR")}</span>
            </div>
            {p.vat_note && <p className="text-[11px] text-slate-400 pt-1">{p.vat_note}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function MonthlyInvoices() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [paying, setPaying] = useState(null);
  const [regInv, setRegInv] = useState(null);
  const [keepCard, setKeepCard] = useState(false);

  async function load() {
    try { setData(await api("/auth/me/monthly-invoices")); }
    catch { setData({ invoices: [], total: 0 }); }
  }
  useEffect(() => { load(); }, []);

  // Only after a FAILED automatic debit: settle with a new card (optionally kept as default).
  async function regularize(inv) {
    setPaying(inv.id);
    try {
      const r = await api(`/monthly-invoices/${inv.access_token}/pay`, { method: "POST", body: { origin_url: window.location.origin, save_default: keepCard }, auth: false });
      if (r.url) goToCheckout(r.url);
    } catch (e) { toast.error(e.message); setPaying(null); }
  }

  async function exportCsv() {
    try {
      const years = [...new Set((data?.invoices || []).map((i) => String(i.period).slice(0, 4)))];
      const year = years.length === 1 ? years[0] : "";
      const csv = await api(`/auth/me/monthly-invoices/export.csv${year ? `?year=${year}` : ""}`);
      const blob = new Blob([typeof csv === "string" ? csv : JSON.stringify(csv)], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `findloop-abonnement-${year || "toutes-annees"}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(t("inv2.exportDone"));
    } catch (e) { toast.error(e.message); }
  }

  if (data === null || data.total === 0) return null;

  return (
    <div className="mb-8" data-testid="monthly-invoices-section">
      <Dialog open={!!regInv} onOpenChange={(o) => !o && setRegInv(null)}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="regularize-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#191A54]">{t("inv.regTitle")}</DialogTitle>
            <DialogDescription>{regInv ? t("inv.regDesc").replace("{amount}", formatMoney(regInv.total_ttc, lang, regInv.currency || "EUR")) : ""}</DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-3 rounded-xl border border-border bg-slate-50 p-3.5 cursor-pointer text-sm text-slate-700" data-testid="regularize-keep-label">
            <input type="checkbox" checked={keepCard} onChange={(e) => setKeepCard(e.target.checked)} className="mt-0.5 h-5 w-5 accent-[#191A54]" data-testid="regularize-keep-card" />
            {t("inv.regKeep")}
          </label>
          <Button onClick={() => regularize(regInv)} disabled={!regInv || paying === regInv?.id} className="w-full h-12 rounded-full bg-[#191A54] hover:bg-[#191A54]/90 font-semibold" data-testid="regularize-pay">
            <CreditCard size={16} className="mr-1.5" /> {t("inv.regPay")}
          </Button>
        </DialogContent>
      </Dialog>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <CalendarClock size={18} className="text-findo-indigo" />
          <h2 className="font-display font-bold text-slate-900">{t("inv2.monthlyTitle")}</h2>
        </div>
        <Button size="sm" variant="outline" onClick={exportCsv} className="rounded-full h-8 text-xs font-semibold" data-testid="monthly-invoices-export">
          <FileSpreadsheet size={13} className="mr-1" /> {t("inv2.exportCsv")}
        </Button>
      </div>
      <div className="bg-white rounded-2xl border border-border card-shadow overflow-hidden divide-y divide-border" data-testid="monthly-invoices-list">
        {data.invoices.map((inv) => {
          const open = expanded === inv.id;
          return (
            <div key={inv.id} data-testid={`monthly-invoice-${inv.id}`}>
              <div className="flex flex-wrap items-center gap-3 p-4 text-sm">
                <span className="font-mono font-semibold text-findo-navy w-36 flex-shrink-0">{inv.number}</span>
                <div className="flex-1 min-w-[220px]">
                  <p className="font-semibold text-slate-800 capitalize">{fmtPeriod(inv.period, lang)}</p>
                  <p className="text-xs text-slate-400">{t("inv2.estCountIssued").replace("{n}", inv.establishment_count).replace("{date}", String(inv.issued_at).slice(0, 10))}{inv.due_at ? t("inv2.dueSuffix").replace("{date}", String(inv.due_at).slice(0, 10)) : ""}</p>
                </div>
                <span className="font-bold text-slate-900">{formatMoney(inv.total_ttc, lang, inv.currency || "EUR")} <span className="text-[11px] font-normal text-slate-400">TTC</span></span>
                <Badge className={inv.status === "paid" ? "bg-emerald-100 text-emerald-700" : inv.status === "failed" ? "bg-red-100 text-red-700" : "bg-sky-100 text-sky-700"} data-testid={`monthly-invoice-status-${inv.id}`}>
                  {inv.status === "paid" ? t("inv2.paid") : inv.status === "failed" ? t("inv.failed") : inv.status === "processing" ? t("inv.processing") : t("inv.autoDebit")}
                </Badge>
                <button onClick={() => setExpanded(open ? null : inv.id)} className="text-slate-400 hover:text-findo-indigo transition-colors" data-testid={`monthly-invoice-toggle-${inv.id}`} title={t("inv2.detailByEst")}>
                  <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                <Button size="sm" variant="outline" onClick={() => window.open(`${API}/monthly-invoices/${inv.access_token}.pdf`, "_blank")} className="rounded-full h-8 text-xs font-semibold" data-testid={`monthly-invoice-pdf-${inv.id}`}>
                  <Eye size={13} className="mr-1" /> {t("inv2.view")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.open(`${API}/monthly-invoices/${inv.access_token}.pdf?download=1`, "_blank")} className="rounded-full h-8 text-xs font-semibold" data-testid={`monthly-invoice-download-${inv.id}`}>
                  <Download size={13} className="mr-1" /> {t("inv2.download")}
                </Button>
                {inv.status === "failed" && (
                  <Button size="sm" onClick={() => { setKeepCard(false); setRegInv(inv); }} disabled={paying === inv.id} className="rounded-full bg-findo-red hover:bg-findo-red/90 h-8 text-xs font-semibold" data-testid={`monthly-invoice-regularize-${inv.id}`}>
                    <CreditCard size={13} className="mr-1" /> {paying === inv.id ? "…" : t("inv.regularize")}
                  </Button>
                )}
              </div>
              {open && (
                <div className="bg-slate-50 px-4 py-3 space-y-1.5" data-testid={`monthly-invoice-lines-${inv.id}`}>
                  {inv.lines.map((l, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      {l.kind === "establishment" ? <Building2 size={12} className="text-slate-400 shrink-0" /> : <Receipt size={12} className="text-findo-indigo shrink-0" />}
                      <span className="flex-1 text-slate-600 truncate">{l.description}</span>
                      <span className={l.included ? "text-slate-400" : "font-semibold text-slate-700"}>
                        {l.included ? t("inv2.included") : `${formatMoney(l.unit_ht, lang)} HT`}
                      </span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between text-xs pt-1.5 border-t border-border mt-1">
                    <span className="text-slate-500">{t("inv2.totalHtTva")}</span>
                    <span className="text-slate-600">{formatMoney(inv.total_ht, lang)} · {formatMoney(inv.total_tva, lang)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm font-bold text-findo-navy">
                    <span>{t("inv2.totalTtc")}</span><span>{formatMoney(inv.total_ttc, lang, inv.currency || "EUR")}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ContractCard() {
  const { t, lang } = useLang();
  const [c, setC] = useState(undefined);
  const [busy, setBusy] = useState(false);
  async function load() { try { setC(await api("/auth/me/contract")); } catch { setC(null); } }
  useEffect(() => { load(); }, []);
  if (!c || !c.contract_expiry) return null;
  const dl = Math.ceil((new Date(c.contract_expiry).getTime() - Date.now()) / 86400000);
  const soon = dl <= 30;
  const fmtD = (iso) => new Date(iso).toLocaleDateString(lang || "en", { day: "2-digit", month: "long", year: "numeric" });
  async function renew() {
    setBusy(true);
    try {
      const r = await api("/auth/me/contract/renew", { method: "POST" });
      toast.success(t("inv2.renewedUntil").replace("{date}", fmtD(r.contract_expiry)));
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  return (
    <div className="mb-8" data-testid="partner-contract-section">
      <div className="flex items-center gap-2 mb-3">
        <CalendarClock size={18} className="text-findo-indigo" />
        <h2 className="font-display font-bold text-slate-900">{t("inv2.contractTitle")}</h2>
      </div>
      <div className={`bg-white rounded-2xl border card-shadow p-4 flex flex-wrap items-center gap-3 ${soon ? "border-amber-300" : "border-border"}`} data-testid="partner-contract-card">
        <div className="flex-1 min-w-0">
          <p className="font-mono font-semibold text-findo-navy">{c.number}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {t("inv2.dueLabel")} <b className={soon ? "text-amber-700" : "text-slate-700"} data-testid="partner-contract-expiry">{fmtD(c.contract_expiry)}</b>
            {dl >= 0 ? ` · J-${dl}` : t("inv2.expired")}
          </p>
        </div>
        {soon && <Badge className="bg-amber-100 text-amber-700" data-testid="partner-contract-soon">{t("inv2.soon")}</Badge>}
        <Button size="sm" onClick={renew} disabled={busy} className="rounded-full bg-findo-navy h-8 text-xs font-semibold" data-testid="partner-contract-renew-btn">
          {busy ? <FindoSpinner size={16} /> : t("inv2.renew")}
        </Button>
      </div>
      {Array.isArray(c.renewals) && c.renewals.length > 0 && (
        <div className="mt-2 rounded-xl bg-slate-50 border border-border p-3" data-testid="partner-contract-renewals">
          <p className="text-xs font-semibold text-slate-600 mb-1">{t("inv2.renewalsTitle").replace("{n}", c.renewals.length)}</p>
          <ul className="text-xs text-slate-500 space-y-0.5">
            {c.renewals.slice().reverse().map((r, i) => (
              <li key={i} className="flex justify-between"><span>{r.at ? fmtD(r.at) : "—"}</span><span>{t("inv2.months").replace("{n}", r.months)}</span></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function Invoices() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");
  const [reminders, setReminders] = useState(null); // {inv, list}

  async function load() {
    try { setData(await api("/auth/me/invoices")); }
    catch (e) { toast.error(e.message); setData({ invoices: [], total: 0, total_paid_eur: 0, total_unpaid_eur: 0 }); }
  }
  useEffect(() => { load(); }, []);

  async function openReminders(inv) {
    try {
      const r = await api(`/auth/me/invoices/${inv.quote_id}/reminders`);
      setReminders({ inv, list: r.reminders || [] });
    } catch (e) { toast.error(e.message); }
  }
  const reminderLabel = (r) => r.kind === "due" ? t("inv.rmDue") : r.kind === "manual" ? t("inv.rmManual") : r.kind === "sms_j30" ? t("inv.rmSms") : r.kind?.startsWith("stage_J") ? `${t("inv.rmStage")} ${r.kind.replace("stage_J", "J+")}` : r.kind;

  function openPdf(path) {
    // Public token routes — open in a new tab.
    window.open(`${API}${path}`, "_blank");
  }

  async function exportCsv() {
    try {
      const yr = yearFilter === "all" ? "" : yearFilter;
      const res = await fetch(`${API}/auth/me/invoices/export.csv?year=${yr}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error(t("inv2.error").replace("{status}", res.status));
      const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `findloop-mes-factures-${yr || "toutes"}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  }

  const allInvoices = data?.invoices || [];
  const years = Array.from(new Set(allInvoices.map((i) => String(i.issued_at).slice(0, 4)).filter(Boolean))).sort().reverse();
  const filtered = allInvoices.filter((i) =>
    (statusFilter === "all" || i.status === statusFilter) &&
    (yearFilter === "all" || String(i.issued_at).slice(0, 4) === yearFilter)
  );
  const btnCls = (active) => `rounded-full px-3 h-8 text-xs font-semibold border ${active ? "bg-findo-navy text-white border-findo-navy" : "bg-white text-slate-600 border-border hover:bg-slate-50"}`;

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      <PageHeader title={t("inv.title")} subtitle={t("inv.subtitle")} testid="invoices-header" />

      <ContractCard />

      <InvoicePreview />

      <MonthlyInvoices />

      {data === null ? (
        <div className="space-y-3" data-testid="invoices-skeleton">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-20 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3 mt-2" data-testid="service-invoices-heading">
            <Receipt size={18} className="text-slate-400" />
            <h2 className="font-display font-bold text-slate-900">{t("inv2.serviceTitle")}</h2>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-6" data-testid="invoices-kpis">
            <div className="bg-white rounded-2xl border border-border card-shadow p-4 text-center">
              <p className="text-xs text-slate-500">{t("inv.count")}</p>
              <p className="text-2xl font-bold text-findo-navy mt-1">{data.total}</p>
            </div>
            <div className="bg-white rounded-2xl border border-border card-shadow p-4 text-center">
              <p className="text-xs text-slate-500">{t("inv.paid")}</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{formatMoney(data.total_paid_eur, lang)}</p>
            </div>
            <div className="bg-white rounded-2xl border border-border card-shadow p-4 text-center">
              <p className="text-xs text-slate-500">{t("inv.unpaid")}</p>
              <p className="text-2xl font-bold text-findo-red mt-1">{formatMoney(data.total_unpaid_eur, lang)}</p>
            </div>
          </div>

          {allInvoices.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="invoices-filters">
              <button className={btnCls(statusFilter === "all")} onClick={() => setStatusFilter("all")} data-testid="filter-status-all">{t("inv.all")}</button>
              <button className={btnCls(statusFilter === "paid")} onClick={() => setStatusFilter("paid")} data-testid="filter-status-paid">{t("inv.statusPaid")}</button>
              <button className={btnCls(statusFilter === "unpaid")} onClick={() => setStatusFilter("unpaid")} data-testid="filter-status-unpaid">{t("inv.statusUnpaid")}</button>
              {years.length > 0 && (
                <select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} className="rounded-full px-3 h-8 text-xs font-semibold border border-border bg-white text-slate-600 ml-auto" data-testid="filter-year">
                  <option value="all">{t("inv.allYears")}</option>
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              )}
              <Button size="sm" variant="outline" onClick={exportCsv} className={`rounded-full h-8 text-xs font-semibold ${years.length > 0 ? "" : "ml-auto"}`} data-testid="invoices-export-btn">
                <Download size={13} className="mr-1" /> {t("inv.exportCsv")}
              </Button>
            </div>
          )}

          {allInvoices.length === 0 ? (
            <div className="text-center py-24 border-2 border-dashed border-border rounded-2xl" data-testid="invoices-empty">
              <FileText size={32} className="mx-auto text-slate-300" />
              <p className="mt-3 text-slate-500">{t("inv.empty")}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl" data-testid="invoices-filtered-empty">
              <p className="text-slate-500">{t("inv.noMatch")}</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-border card-shadow overflow-hidden divide-y divide-border" data-testid="partner-invoices-list">
              {filtered.map((inv) => (
                <div key={inv.quote_id} className="flex flex-wrap items-center gap-3 p-4 text-sm" data-testid="partner-invoice-row">
                  <span className="font-mono font-semibold text-findo-navy w-32 flex-shrink-0">{inv.number}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-400">{t("inv.issued")} {String(inv.issued_at).slice(0, 10)}
                      {inv.status === "paid" && inv.paid_at ? ` · ${t("inv.paidOn")} ${String(inv.paid_at).slice(0, 10)}` : inv.due_at ? ` · ${t("inv.due")} ${String(inv.due_at).slice(0, 10)}` : ""}
                    </p>
                  </div>
                  <span className="font-bold text-slate-900">{formatMoney(inv.amount_ttc, lang, inv.currency || "EUR")}</span>
                  <Badge className={inv.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"} data-testid={`partner-invoice-status-${inv.quote_id}`}>
                    {inv.status === "paid" ? t("inv.statusPaid") : t("inv.statusUnpaid")}
                  </Badge>
                  <Button size="sm" variant="outline" onClick={() => openPdf(`/quotes/${inv.access_token}/invoice.pdf`)} className="rounded-full h-8 text-xs font-semibold" data-testid={`partner-invoice-pdf-${inv.quote_id}`}>
                    <Download size={13} className="mr-1" /> {t("inv.invoicePdf")}
                  </Button>
                  {inv.reminder_count > 0 && (
                    <Button size="sm" variant="outline" onClick={() => openReminders(inv)} className="rounded-full h-8 text-xs font-semibold" data-testid={`partner-invoice-reminders-${inv.quote_id}`}>
                      <BellRing size={13} className="mr-1" /> {t("inv.reminders")} ({inv.reminder_count})
                    </Button>
                  )}
                  {inv.status === "paid" ? (
                    <Button size="sm" variant="outline" onClick={() => openPdf(`/quotes/${inv.access_token}/receipt.pdf`)} className="rounded-full h-8 text-xs font-semibold" data-testid={`partner-invoice-receipt-${inv.quote_id}`}>
                      <Receipt size={13} className="mr-1" /> {t("inv.receipt")}
                    </Button>
                  ) : inv.debit_failed ? (
                    <Button size="sm" onClick={() => window.open(`/devis/${inv.access_token}`, "_blank")} className="rounded-full bg-findo-red hover:bg-findo-red/90 h-8 text-xs font-semibold" data-testid={`partner-invoice-regularize-${inv.quote_id}`}>
                      <CreditCard size={13} className="mr-1" /> {t("inv.regularize")}
                    </Button>
                  ) : (
                    <span className="text-xs font-semibold text-slate-500" data-testid={`partner-invoice-debit-${inv.quote_id}`}>
                      {inv.debit_status === "retry" ? t("sd.retryOn").replace("{date}", (inv.debit_next_at || "").slice(0, 10))
                        : ["processing", "pending"].includes(inv.debit_status) ? t("sd.processing")
                        : t("sd.autoOn").replace("{date}", (inv.due_at || "").slice(0, 10))}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
      <Dialog open={!!reminders} onOpenChange={(o) => !o && setReminders(null)}>
        <DialogContent data-testid="partner-reminders-dialog">
          <DialogHeader>
            <DialogTitle>{t("inv.reminders")} — {reminders?.inv?.number}</DialogTitle>
            <DialogDescription>{t("inv.remindersDesc")}</DialogDescription>
          </DialogHeader>
          {reminders?.list?.length ? (
            <div className="divide-y divide-border max-h-80 overflow-auto" data-testid="partner-reminders-list">
              {reminders.list.map((r) => (
                <div key={r.id} className="flex items-center gap-2 py-2.5 text-sm" data-testid="partner-reminder-row">
                  <Badge className={r.channel === "sms" ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}>{r.channel === "sms" ? "SMS" : t("a.email")}</Badge>
                  <span className="flex-1 text-slate-700">{reminderLabel(r)}</span>
                  <span className="text-xs text-slate-400">{new Date(r.at).toLocaleString(lang || "en")}</span>
                </div>
              ))}
            </div>
          ) : <p className="py-6 text-center text-slate-400 text-sm" data-testid="partner-reminders-empty">{t("inv.remindersEmpty")}</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
